import { generatePath } from "react-router-dom";
import { stringifyQuery } from "@evg-ui/lib/src/utils/query-string";
import { reportError } from "@evg-ui/lib/utils/errorReporting";
import { getGithubCommitUrl } from "constants/externalResources";
import { WaterfallFilterOptions } from "pages/waterfall/types";
import { HistoryQueryParams, TestStatus } from "types/history";
import { ConfigurePatchPageTabs, VersionPageTabs } from "types/patch";
import { LogTypes, TaskTab } from "types/task";
import { ProjectTriggerLevel } from "types/triggers";
import { toArray } from "utils/array";

export enum PageNames {
  HTMLLog = "html-log",
  TestHTMLLog = "test-html-log",
  Diff = "diff",
  FileDiff = "file-diff",
  Patches = "patches",
  Settings = "settings",
}

export enum SpawnTab {
  Host = "host",
  Volume = "volume",
}

export enum PreferencesTabRoutes {
  Profile = "profile",
  Notifications = "notifications",
  CLI = "cli",
  UISettings = "ui-settings",
  PublicKeys = "publickeys",
  SageBotSettings = "sage-bot-settings",
}

export enum ImageTabRoutes {
  BuildInformation = "build-information",
  EventLog = "event-log",
}

// Enums should be ordered in view order.
export enum ProjectSettingsTabRoutes {
  General = "general",
  Access = "access",
  Variables = "variables",
  Notifications = "notifications",
  PatchAliases = "patch-aliases",
  VirtualWorkstation = "virtual-workstation",
  ViewsAndFilters = "views-and-filters",
  ProjectTriggers = "project-triggers",
  PeriodicBuilds = "periodic-builds",
  TestSelection = "test-selection",
  TaskOwnershipAndFoliage = "task-ownership-and-foliage",
  Plugins = "plugins",
  PullRequests = "pull-requests",
  CommitChecks = "commit-checks",
  MergeQueue = "merge-queue",
  GitTags = "git-tags",
  GithubAppSettings = "github-app-settings",
  GithubPermissionGroups = "github-permission-groups",
  EventLog = "event-log",
}

export enum AdminSettingsGeneralSection {
  Announcements = "announcements",
  Runners = "runners",
  Web = "web",
  Authentication = "authentication",
  ExternalCommunications = "external-communications",
  BackgroundProcessing = "background-processing",
  Providers = "providers",
  Other = "other",
}

export enum AdminSettingsTabRoutes {
  General = "general",
  ServiceFlags = "service-flags",
  RestartTasks = "restart-tasks",
  EventLog = "event-log",
}

export enum DistroSettingsTabRoutes {
  General = "general",
  Provider = "provider",
  Task = "task",
  Host = "host",
  Project = "project",
  EventLog = "event-log",
  SingleTaskDistros = "single-task-distros",
}
const paths = {
  adminSettings: "/admin-settings",
  container: "/container",
  distro: "/distro",
  distros: "/distros",
  host: "/host",
  hosts: "/hosts",
  image: "/image",
  jobLogs: "/job-logs",
  login: "/login",
  patch: "/patch",
  preferences: "/preferences",
  project: "/project",
  projects: "/projects",
  repo: "/repo",
  spawn: "/spawn",
  task: "/task",
  taskQueue: "/task-queue",
  user: "/user",
  variantHistory: "/variant-history",
  version: "/version",
  waterfall: "/waterfall",
};

export enum slugs {
  distroId = "distroId",
  execution = "execution",
  fileName = "fileName",
  groupId = "groupId",
  hostId = "hostId",
  imageId = "imageId",
  patchId = "patchId",
  projectIdentifier = "projectIdentifier",
  repoId = "repoId",
  tab = "tab",
  taskId = "taskId",
  taskName = "taskName",
  variantName = "variantName",
  versionId = "versionId",
  userId = "userId",
}
export const idSlugs = [
  slugs.distroId,
  slugs.hostId,
  slugs.imageId,
  slugs.patchId,
  slugs.projectIdentifier,
  slugs.taskId,
  slugs.versionId,
  slugs.userId,
];

export const redirectRoutes = {
  distroSettings: paths.distros,
  patch: `${paths.patch}/:${slugs.versionId}`,
  projectSettings: paths.projects,
  userPatches: `${paths.user}/:${slugs.userId}`,
  waterfall: `${paths.waterfall}/:${slugs.projectIdentifier}`,
  legacyCommits: `commits/:${slugs.projectIdentifier}`,
};

export const routes = {
  adminSettings: paths.adminSettings,
  configurePatch: `${paths.patch}/:${slugs.patchId}/configure/:${slugs.tab}?`,
  distroSettings: `${paths.distro}/:${slugs.distroId}/${PageNames.Settings}`,
  host: `${paths.host}/:${slugs.hostId}`,
  hosts: paths.hosts,
  image: `${paths.image}/:${slugs.imageId}`,
  jobLogs: paths.jobLogs,
  login: paths.login,
  myPatches: `${paths.user}/${PageNames.Patches}`,
  preferences: paths.preferences,
  projectPatches: `${paths.project}/:${slugs.projectIdentifier}/${PageNames.Patches}`,
  projectSettings: `${paths.project}/:${slugs.projectIdentifier}/${PageNames.Settings}`,
  repoSettings: `${paths.repo}/:${slugs.repoId}/${PageNames.Settings}`,
  spawn: paths.spawn,
  spawnHost: `${paths.spawn}/${SpawnTab.Host}`,
  spawnVolume: `${paths.spawn}/${SpawnTab.Volume}`,
  task: `${paths.task}/:${slugs.taskId}/:${slugs.tab}?`,
  taskHTMLLog: `${paths.task}/:${slugs.taskId}/${PageNames.HTMLLog}`,
  testHTMLLog: `${paths.task}/:${slugs.taskId}/${PageNames.TestHTMLLog}`,
  versionDiff: `${paths.version}/:${slugs.versionId}/${PageNames.Diff}`,
  versionFileDiff: `${paths.version}/:${slugs.versionId}/${PageNames.FileDiff}`,
  taskQueue: `${paths.taskQueue}/:${slugs.distroId}?`,
  user: paths.user,
  userPatches: `${paths.user}/:${slugs.userId}/${PageNames.Patches}`,
  variantHistory: `${paths.variantHistory}/:${slugs.projectIdentifier}/:${slugs.variantName}`,
  version: `${paths.version}/:${slugs.versionId}/:${slugs.tab}?`,
  waterfall: `${paths.project}/:${slugs.projectIdentifier}/waterfall`,
};

// Route patterns augmented with optional tab/param segments so deep links resolve
// to one route name. Shared so the span processor and analytics agree.
export const observabilityRouteConfig = {
  ...routes,
  projectSettings: `${routes.projectSettings}/:${slugs.tab}?`,
  image: `${routes.image}/:${slugs.tab}?`,
  distroSettings: `${routes.distroSettings}/:${slugs.tab}?`,
  preferences: `${routes.preferences}/:${slugs.tab}?`,
  spawn: `${routes.spawn}/:${slugs.tab}?`,
  jobLogs: `${routes.jobLogs}/:${slugs.taskId}/:${slugs.execution}/:${slugs.groupId}`,
  patchRedirect: redirectRoutes.patch,
};

export const getUserPatchesRoute = (userId: string): string =>
  `${paths.user}/${userId}/${PageNames.Patches}`;

export const getVersionRoute = (
  versionId: string,
  options?: {
    tab?: VersionPageTabs;
    variant?: string;
    page?: number;
    statuses?: string[];
    sorts?: string | string[];
  },
) => {
  const { tab, ...rest } = options || {};
  const queryParams = stringifyQuery({
    ...rest,
  });
  return `${paths.version}/${versionId}/${tab ?? VersionPageTabs.Tasks}${
    queryParams && `?${queryParams}`
  }`;
};

export const getPatchRoute = (
  patchId: string,
  options: {
    tab?: ConfigurePatchPageTabs;
    configure: boolean;
  },
) => {
  const { configure, tab, ...rest } = options || {};
  const queryParams = stringifyQuery({
    ...rest,
  });
  if (!configure) return getVersionRoute(patchId);
  return `${paths.patch}/${patchId}/configure/${
    tab ?? ConfigurePatchPageTabs.Tasks
  }${queryParams && `?${queryParams}`}`;
};

export const getHostRoute = (hostId: string) => `${paths.host}/${hostId}`;

export const getAllHostsRoute = (options?: {
  hostId?: string;
  distroId?: string;
  statuses?: string[];
  currentTaskId?: string;
  startedBy?: string;
}) => {
  const { ...rest } = options || {};
  const queryParams = stringifyQuery({
    ...rest,
  });
  return `${paths.hosts}?${queryParams}`;
};

export type GetTaskRouteOptions = {
  tab?: TaskTab;
  execution?: number;
  [key: string]: unknown;
};
export const getTaskRoute = (taskId: string, options?: GetTaskRouteOptions) => {
  const { tab, ...rest } = options || {};
  const queryParams = stringifyQuery({
    ...rest,
  });
  return `${paths.task}/${taskId}${tab ? `/${tab}` : ""}${
    queryParams ? `?${queryParams}` : ""
  }`;
};

export const getTaskHTMLLogRoute = (
  taskId: string,
  execution: number,
  origin: LogTypes,
) => {
  const queryParams = stringifyQuery({
    execution,
    origin,
  });
  return generatePath(`${routes.taskHTMLLog}?${queryParams}`, { taskId });
};

export const getTestHTMLLogRoute = (
  taskId: string,
  execution: number,
  testName: string,
  groupId?: string,
  lineNum?: number,
) => {
  const queryParams = stringifyQuery({
    execution,
    testName,
    ...(groupId && { groupId }),
  });

  let path = generatePath(`${routes.testHTMLLog}?${queryParams}`, { taskId });
  if (typeof lineNum !== "undefined") {
    path += `#L${lineNum}`;
  }
  return path;
};

export const getVersionDiffRoute = (versionId: string, moduleIndex: number) => {
  const queryParams = stringifyQuery({
    patch_number: moduleIndex,
  });
  return generatePath(`${routes.versionDiff}?${queryParams}`, {
    versionId,
  });
};

export const getFileDiffRoute = (
  versionId: string,
  fileName: string,
  patchNumber?: number,
) => {
  const queryParams = {
    file_name: fileName,
    ...(patchNumber !== undefined && { patch_number: patchNumber }),
  };
  const queryString = stringifyQuery(queryParams);
  return generatePath(`${routes.versionFileDiff}?${queryString}`, {
    versionId,
  });
};

export const getPreferencesRoute = (tab?: PreferencesTabRoutes) =>
  `${paths.preferences}/${tab}`;

export const getTaskQueueRoute = (distro: string, taskId?: string) => {
  const queryParams = stringifyQuery({
    taskId,
  });
  return `${paths.taskQueue}/${distro}${taskId ? `?${queryParams}` : ""}`;
};
export const getSpawnHostRoute = ({
  distroId,
  host,
  spawnHost,
  taskId,
}: {
  distroId?: string;
  host?: string;
  taskId?: string;
  spawnHost?: boolean;
}) => {
  const queryParams = stringifyQuery({
    ...(spawnHost && { spawnHost: "True" }),
    distroId,
    taskId,
    host,
  });
  return `${routes.spawnHost}?${queryParams}`;
};

export const getSpawnVolumeRoute = (volume: string) => {
  const queryParams = stringifyQuery({
    volume,
  });
  return `${routes.spawnVolume}?${queryParams}`;
};

export const getProjectPatchesRoute = (projectIdentifier: string) =>
  `${paths.project}/${encodeURIComponent(projectIdentifier)}/${
    PageNames.Patches
  }`;

export const getImageRoute = (
  imageId: string,
  tab?: ImageTabRoutes,
  anchor?: string,
) =>
  `${paths.image}/${imageId}/${tab ?? ImageTabRoutes.BuildInformation}${anchor ? `#${anchor}` : ""}`;

export const getProjectSettingsRoute = (
  projectId: string,
  tab?: ProjectSettingsTabRoutes,
) => {
  // Encode projectId for backwards compatibilty.
  // Encoding can be removed when all projectIDs are URL friendly without encoding
  const encodedProjectId = encodeURIComponent(projectId);
  const root = `${paths.project}/${encodedProjectId}/${PageNames.Settings}`;
  return tab ? `${root}/${tab}` : root;
};

export const getRepoSettingsRoute = (
  repoId: string,
  tab?: ProjectSettingsTabRoutes,
) => {
  const root = `${paths.repo}/${repoId}/${PageNames.Settings}`;
  return tab ? `${root}/${tab}` : root;
};

export const getDistroSettingsRoute = (
  distroId: string,
  tab?: DistroSettingsTabRoutes,
) =>
  tab
    ? `${paths.distro}/${distroId}/${PageNames.Settings}/${tab}`
    : `${paths.distro}/${distroId}/${PageNames.Settings}/${DistroSettingsTabRoutes.General}`;

export const getWaterfallRoute = (
  projectIdentifier?: string,
  options?: {
    statusFilters?: string[];
    variantFilters?: string[];
    requesterFilters?: string[];
    taskFilters?: string[];
    revision?: string;
  },
) => {
  const {
    requesterFilters,
    revision,
    statusFilters,
    taskFilters,
    variantFilters,
  } = options || {};
  const queryParams = stringifyQuery({
    [WaterfallFilterOptions.Statuses]: statusFilters,
    [WaterfallFilterOptions.Task]: taskFilters,
    [WaterfallFilterOptions.Requesters]: requesterFilters,
    [WaterfallFilterOptions.BuildVariant]: variantFilters,
    [WaterfallFilterOptions.Revision]: revision,
  });
  return `${paths.project}/${encodeURIComponent(projectIdentifier ?? "")}${paths.waterfall}${queryParams ? `?${queryParams}` : ""}`;
};

const getHistoryRoute = (
  basePath: string,
  filters?: {
    failingTests?: string[];
    passingTests?: string[];
  },
  selectedCommit?: number,
  visibleColumns?: string[],
  taskId?: string,
) => {
  if (filters || selectedCommit || visibleColumns || taskId) {
    const failingTests = toArray(filters?.failingTests);
    const passingTests = toArray(filters?.passingTests);

    const queryParams = stringifyQuery({
      [TestStatus.Failed]: failingTests,
      [TestStatus.Passed]: passingTests,
      [HistoryQueryParams.SelectedCommit]: selectedCommit,
      [HistoryQueryParams.TaskID]: taskId,
      [HistoryQueryParams.VisibleColumns]: visibleColumns,
    });
    return `${basePath}?${queryParams}`;
  }
  return basePath;
};
export const getVariantHistoryRoute = (
  projectIdentifier: string,
  variantName: string,
  options?: {
    filters?: {
      failingTests?: string[];
      passingTests?: string[];
    };
    selectedCommit?: number;
    visibleColumns?: string[];
  },
) => {
  const { filters, selectedCommit, visibleColumns } = options || {};
  return getHistoryRoute(
    `${paths.variantHistory}/${encodeURIComponent(
      projectIdentifier,
    )}/${encodeURIComponent(variantName)}`,
    filters,
    selectedCommit,
    visibleColumns,
  );
};

export const getTriggerRoute = ({
  triggerType,
  upstreamOwner,
  upstreamRepo,
  upstreamRevision,
  upstreamTask,
  upstreamVersion,
}: {
  triggerType: string;
  upstreamTask?: { id: string } | null;
  upstreamVersion?: { id: string } | null;
  upstreamRevision: string;
  upstreamOwner: string;
  upstreamRepo: string;
}) => {
  if (triggerType === ProjectTriggerLevel.TASK && upstreamTask) {
    return getTaskRoute(upstreamTask.id);
  }
  if (triggerType === ProjectTriggerLevel.PUSH) {
    return getGithubCommitUrl(upstreamOwner, upstreamRepo, upstreamRevision);
  }

  const upstreamVersionId = upstreamVersion?.id;
  if (!upstreamVersionId) {
    reportError(new Error("No upstream version found for route"));
  }

  return getVersionRoute(upstreamVersion?.id ?? "");
};

export const getAdminSettingsRoute = (
  tab?: AdminSettingsTabRoutes,
  anchor?: string,
) => {
  const pathName = tab
    ? `${paths.adminSettings}/${tab}`
    : `${paths.adminSettings}/${AdminSettingsTabRoutes.General}`;

  if (anchor && pathName.includes(AdminSettingsTabRoutes.General)) {
    return `${pathName}#${anchor}`;
  }
  return pathName;
};
